// Import necessary libraries and components
import React, { useMemo, useState, useCallback } from 'react'

// Import Material-UI components
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'

// Import constants and utility functions
import { CONTENT } from '../../constants/constants'
import { charSet, guid } from '../../utils'

// Import custom components and hooks
import Form from '../common/Form'
import FormHeader from '../common/FormHeader'
import { useAxiosPrivate } from '../../hooks'
import { request } from '../../api/axios'
import { API_URL } from '../../api/constants'
import InputFieldWithAction from '../InputFieldWithAction'
import LoadingButton from '../common/LoadingButton'
import Snackbar from '../common/Snackbar'

// Helper function to extract required fields data from a data source
const getRequiredFieldsData = (fields, dataSource) =>
  fields.reduce(
    (requiredData, { name }) => ({
      ...requiredData,
      [name]: dataSource[name] || '',
    }),
    {}
  )

// Helper function to generate a code asynchronously
const generateCode = (length, charSet) =>
  new Promise((resolve) => {
    setTimeout(() => resolve(guid(length, charSet)), 500)
  })

// Function to sync data with Kardia Admin API
const syncInteropAdminWithKardiaAdmin = async (teamId, axiosInstance) => {
  const response = await request({
    axiosInstance,
    path: `${API_URL.TEAMS}/${teamId}`,
  })
  if (response.status === 200) {
    return response.data
  }
  throw new Error('Failed to fetch data')
}

// Main component for Facility Details Form
const FacilityDetailsForm = ({
  prefillData, // Data to prefill the form
  loading, // Loading state for the form
  error, // Error state for the form
  onSubmit, // Callback for form submission
  readOnlyFields, // Fields that should be read-only
  viewMode, // Mode of the form (e.g., view or edit)
  triggerOnMountFields = [], // Fields to trigger actions on mount
}) => {
  const axiosPrivate = useAxiosPrivate() // Custom hook for Axios instance

  // State variables
  const [teamId, setTeamId] = useState(prefillData?.kpro_team_id || null) // Team ID state
  const [syncInProgress, setSyncInProgress] = useState(false) // Sync progress state
  const [syncError, setSyncError] = useState(null) // Sync error state
  const [facilityDataFromKardiaAdmin, setFacilityDataFromKardiaAdmin] =
    useState({}) // Synced facility data

  // Function to sync facility data with Kardia Admin
  const syncWithKardiaAdminData = useCallback(async () => {
    try {
      setSyncInProgress(true)
      if (!teamId) throw new Error('NO_TEAM_ID')
      const response = await syncInteropAdminWithKardiaAdmin(
        teamId,
        axiosPrivate
      )
      setFacilityDataFromKardiaAdmin({
        facility_name: response.name,
        connected_application: response.connected_application,
        practice_timezone: response.practice_timezone,
        kpro_team_id: response.kpro_id,
      })
    } catch (error) {
      setSyncError(
        error.message === 'NO_TEAM_ID'
          ? 'Please select the KPro Team to sync the data'
          : 'Something went wrong'
      )
    } finally {
      setSyncInProgress(false)
    }
  }, [teamId, axiosPrivate])

  // Define form fields
  const fields = useMemo(
    () => [
      {
        label: 'K-Pro-Team',
        name: 'kpro_team_id',
        hint: 'Search with K-Pro Team Id or Facility Name',
        required: true,
      },
      { label: 'Facility Name', name: 'facility_name', required: true },
      { label: 'Street', name: 'street' },
      { label: 'Practice timezone', name: 'practice_timezone', required: true },
      { label: 'City', name: 'city' },
      { label: 'State', name: 'state' },
      { label: 'Country', name: 'country' },
      { label: 'Postal Code', name: 'postal_code' },
      { label: 'Connected Application', name: 'connected_application' },
      {
        label: 'Facility Code',
        name: 'facility_code',
        component: (params) => (
          <InputFieldWithAction
            {...params}
            triggerOnMount={triggerOnMountFields.includes(params?.name)}
            action={() => generateCode(5, charSet.ALPHA_UPPER)}
          />
        ),
      },
      {
        label: 'X-API-Key',
        name: 'xapi_key',
        charSet: charSet.ALPHANUMERIC,
        length: 32,
        component: (params) => (
          <InputFieldWithAction
            {...params}
            triggerOnMount={triggerOnMountFields.includes(params?.name)}
            action={() => generateCode(32, charSet.ALPHANUMERIC)}
          />
        ),
      },
    ],
    [triggerOnMountFields]
  )

  // Compute initial form data
  const initialData = useMemo(
    () =>
      getRequiredFieldsData(fields, {
        ...prefillData,
        ...facilityDataFromKardiaAdmin,
      }),
    [prefillData, facilityDataFromKardiaAdmin, fields]
  )

  const isViewMode = viewMode === 'VIEW' // Check if the form is in view mode

  return (
    <Paper sx={{ padding: 3 }}>
      {/* Form header with sync button */}
      <FormHeader
        marginBottom={2}
        {...CONTENT.facility_details}
        action={
          <LoadingButton
            type="button"
            variant="outlined"
            disabled={syncInProgress || isViewMode}
            loading={syncInProgress}
            onClick={syncWithKardiaAdminData}
          >
            Sync Facility With Kardia Admin
          </LoadingButton>
        }
      />
      <Divider />
      {/* Main form component */}
      <Form
        fields={fields}
        initialData={initialData}
        getTeamId={setTeamId}
        readOnlyFields={readOnlyFields}
        columnCount={2}
        onSubmit={onSubmit}
        loading={loading}
        error={error}
        viewMode={viewMode}
        disabled={syncInProgress || isViewMode}
      />
      {/* Snackbar for displaying errors */}
      <Snackbar
        open={!!syncError}
        children={syncError}
        severity="error"
        onClose={() => setSyncError(null)}
      />
    </Paper>
  )
}

export default FacilityDetailsForm
